import React, { forwardRef } from 'react'
import clsx from 'clsx'

interface ISubtitleProps {
  children: React.ReactNode
  className?: string
  smallMargin?: boolean
}

const PlusIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <line x1="6" y1="2" x2="6" y2="10" />
    <line x1="2" y1="6" x2="10" y2="6" />
  </svg>
)

const Subtitle = forwardRef<HTMLHeadingElement, ISubtitleProps>(
  ({ children, className = '', smallMargin = false }, ref) => {
    const subtitleClasses = clsx(
      // Layout & Alignment
      'inline-flex items-center w-fit max-w-max select-none',
      'gap-extra-small md:gap-small',

      // Shape & Padding
      'rounded-constant-full',
      'px-small py-pico xl:px-base xl:py-pico',

      // Surface & Colors
      'bg-elevation-lower dark:bg-surface-disabled text-on-surface transition-colors duration-300',

      // Typography
      'font-medium font-palanquinDark uppercase',
      'text-body-small xl:text-body-medium',

      // Dynamic spacing (disabled if explicit mb- class provided in className)
      smallMargin ? 'mb-extra-small' : className.includes('mb-') ? '' : 'mb-base md:mb-semi-large lg:mb-small',

      className
    )

    const iconBadgeClasses = clsx(
      // Dimensions & Shape
      'w-base h-base lg:w-3.5 lg:h-3.5 xl:w-base xl:h-base rounded-container-extra-small shrink-0',

      // Flex centering
      'inline-flex items-center justify-center',

      // Colors
      'bg-primary text-on-primary-unstable'
    )

    return (
      <h4 ref={ref} className={subtitleClasses}>
        <span className={iconBadgeClasses} aria-hidden="true">
          <PlusIcon className="w-extra-small h-extra-small" />
        </span>
        <span>{children}</span>
      </h4>
    )
  }
)

Subtitle.defaultProps = {
  className: '',
  smallMargin: false,
}

export default Subtitle
